/**
 * ArchLang v0.10 — shared label-axis builder for the tabular representations
 * (`matrix` DSM and `grid` cross-tab). An axis is either the raw module-key axis
 * (no getter) or a getter-GROUPED axis whose labels are the getter's distinct
 * values. An aspect getter is list-aware: a module carrying the key several times
 * joins each group (`labelsOf` maps a module key to ≥1 axis labels). Pure over the
 * derived model — no scene types, no rendering.
 */

import type { Value } from "./types.js";
import type { ExprGetter } from "./selector-ast.js";
import type { ElementNode, EvalContext } from "./selector-eval.js";
import { evalExpression, UnsupportedExprError } from "./expression-eval.js";

export interface Axis {
  /** Ordered axis labels (module keys, or grouped getter values). */
  readonly labels: readonly string[];
  /** Module key → the axis label(s) it contributes to (≥1 for a list aspect). */
  readonly labelsOf: ReadonlyMap<string, readonly string[]>;
  /** True when the axis is a grouped getter axis (labels ≠ module keys). */
  readonly grouped: boolean;
}

/** Build one axis: a grouped getter axis, or the raw module-key axis. */
export function buildAxis(
  getter: ExprGetter | undefined,
  modules: readonly ElementNode[],
  ctx: EvalContext,
): Axis {
  if (!getter) {
    const labels = modules.map((m) => m.key).sort();
    const labelsOf = new Map<string, string[]>(labels.map((k) => [k, [k]]));
    return { labels, labelsOf, grouped: false };
  }
  const labelsOf = new Map<string, string[]>();
  const distinct = new Set<string>();
  for (const m of modules) {
    const vals = axisValuesOf(getter, m, ctx);
    if (vals.length === 0) continue; // no value → contributes to no group
    labelsOf.set(m.key, vals);
    for (const v of vals) distinct.add(v);
  }
  return { labels: [...distinct].sort(), labelsOf, grouped: true };
}

/**
 * The axis value(s) a module contributes under a getter. An `@@aspect` getter is
 * list-aware (a module carrying the key several times joins each group); a field
 * / builtin getter yields at most one value. Missing / deferred → `[]`.
 */
export function axisValuesOf(getter: ExprGetter, node: ElementNode, ctx: EvalContext): string[] {
  if (getter.sigil === "@@" && getter.capabilityArgs === undefined) {
    const vals = node.aspects.get(getter.path.join("."));
    if (!vals || vals.length === 0) return [];
    return [...new Set(vals.map(aspectLabel).filter((s): s is string => s !== undefined))];
  }
  try {
    const v = evalExpression(getter, node, ctx);
    return v === undefined ? [] : [String(v)];
  } catch (e) {
    if (e instanceof UnsupportedExprError) return [];
    throw e;
  }
}

/** A model aspect `Value` → its group label (mirrors the projection's grouping). */
export function aspectLabel(v: Value): string | undefined {
  switch (v.kind) {
    case "string":
    case "identifier":
      return v.text;
    case "ref":
      return v.instance ? `${v.path.join(".")} as ${v.instance}` : v.path.join(".");
    case "number":
      return String(v.value);
    case "boolean":
      return String(v.value);
    case "list":
      return undefined; // nested list — not a scalar label
    case "expr":
      // Unreachable on a cascaded model (field-exprs resolves every getter/cast
      // before projection); spelled out rather than left to a `default` so the
      // compiler points here if the value grammar grows again.
      return undefined;
  }
}
